import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { widgetsApi, statsApi } from '../api/client';
import { JsonEditor, formatJson } from '../components/JsonEditor';

const API_URL = import.meta.env.VITE_API_URL || '';

interface QuizContent {
  hook_title: string;
  questions: Array<{
    question: string;
    options: string[];
    correct_index: number;
    explanation: string;
  }>;
}

interface PollContent {
  hook_title: string;
  question: string;
  options: string[];
}

export default function WidgetEditor() {
  const { siteId, widgetId } = useParams<{ siteId: string; widgetId: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [editedContent, setEditedContent] = useState<string>('');
  const [status, setStatus] = useState<string>('active');
  const [hasChanges, setHasChanges] = useState(false);
  const [regenerateType, setRegenerateType] = useState<string>('auto');

  const { data, isLoading, error } = useQuery({
    queryKey: ['widget', widgetId],
    queryFn: () => widgetsApi.get(widgetId!),
    enabled: !!widgetId,
  });

  // Check WordPress sync status
  const { data: wpSyncData } = useQuery({
    queryKey: ['widget-wp-sync', widgetId],
    queryFn: () => widgetsApi.checkWpSync(widgetId!),
    enabled: !!widgetId,
  });

  const updateMutation = useMutation({
    mutationFn: (data: { content?: string; status?: string }) =>
      widgetsApi.update(widgetId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widget', widgetId] });
      queryClient.invalidateQueries({ queryKey: ['site-widgets', siteId] });
      setHasChanges(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => widgetsApi.delete(widgetId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-widgets', siteId] });
      queryClient.invalidateQueries({ queryKey: ['latestWidgets'] });
      navigate(`/sites/${siteId}`);
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: (type?: string) => widgetsApi.regenerate(widgetId!, type),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['widget', widgetId] });
      queryClient.invalidateQueries({ queryKey: ['site-widgets', siteId] });
      setEditedContent(data.widget.content);
      setHasChanges(false);
    },
  });

  const widget = data?.widget;

  useEffect(() => {
    if (widget) {
      // Pretty-print the JSON on load
      setEditedContent(formatJson(widget.content));
      setStatus(widget.status);
      setRegenerateType(widget.type);
    }
  }, [widget]);

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (error || !widget) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Widget not found</p>
        <Link to={`/sites/${siteId}`} className="btn btn-primary">
          Back to Site
        </Link>
      </div>
    );
  }

  let parsedContent: QuizContent | PollContent | null = null;
  try {
    parsedContent = JSON.parse(editedContent);
  } catch {
    // Invalid JSON
  }

  function handleContentChange(newContent: string) {
    setEditedContent(newContent);
    setHasChanges(true);
  }

  function handleSave() {
    try {
      JSON.parse(editedContent); // Validate JSON
      updateMutation.mutate({ content: editedContent, status });
    } catch {
      alert('Invalid JSON content');
    }
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <Link to={`/sites/${siteId}/widgets`} className="text-gray-400 hover:text-gray-600">
            ← Widgets
          </Link>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Edit Widget</h1>
          <span className="px-2 py-1 text-sm bg-gray-100 rounded capitalize">
            {widget.type}
          </span>
          {/* WordPress sync status */}
          {wpSyncData?.synced === true && (
            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
              ✓ Active in WP
            </span>
          )}
          {wpSyncData?.synced === false && (
            <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full" title={`WordPress has: ${wpSyncData.wpWidgetId || 'none'}`}>
              ⚠ Not active in WP
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setHasChanges(true);
            }}
            className="input w-auto"
          >
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            className="btn bg-red-50 text-red-600 hover:bg-red-100 border-red-200"
            onClick={() => {
              if (confirm('Are you sure you want to delete this widget? This cannot be undone.')) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      {(updateMutation.isError || regenerateMutation.isError || deleteMutation.isError) && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
          {updateMutation.isError && <>Failed to save: {updateMutation.error.message}</>}
          {regenerateMutation.isError && <>Failed to regenerate: {regenerateMutation.error.message}</>}
          {deleteMutation.isError && <>Failed to delete: {deleteMutation.error.message}</>}
        </div>
      )}

      {updateMutation.isSuccess && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6">
          Changes saved successfully!
        </div>
      )}

      {regenerateMutation.isSuccess && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6">
          Content regenerated successfully!
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Content (JSON)</h2>
            <div className="flex gap-2 items-center">
              <button
                className="btn btn-secondary text-sm"
                onClick={() => handleContentChange(formatJson(editedContent))}
                title="Format JSON"
              >
                Format
              </button>
              <select
                value={regenerateType}
                onChange={(e) => setRegenerateType(e.target.value)}
                disabled={regenerateMutation.isPending}
                className="input w-auto text-sm"
              >
                <option value="auto">Auto</option>
                <option value="quiz">Quiz</option>
                <option value="poll">Poll</option>
              </select>
              <button
                className="btn btn-secondary text-sm"
                onClick={() => {
                  if (confirm('This will replace all current content with freshly generated questions. Continue?')) {
                    regenerateMutation.mutate(regenerateType);
                  }
                }}
                disabled={regenerateMutation.isPending}
              >
                {regenerateMutation.isPending ? 'Regenerating...' : '↻ Regenerate'}
              </button>
            </div>
          </div>
          <JsonEditor
            value={editedContent}
            onChange={handleContentChange}
            disabled={regenerateMutation.isPending}
          />
          <p className="text-xs text-gray-500 mt-2">
            Edit the JSON directly to fix AI mistakes or tweak content
          </p>
        </div>

        {/* Preview */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
          {parsedContent ? (
            <WidgetPreview type={widget.type} content={parsedContent} />
          ) : (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg">
              Invalid JSON - cannot preview
            </div>
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="card mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Widget Info</h2>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Widget ID</span>
            <p className="font-mono">{widget.id}</p>
          </div>
          <div>
            <span className="text-gray-500">Source URL</span>
            <p>
              <a
                href={widget.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline"
              >
                {widget.source_url}
              </a>
            </p>
          </div>
          <div>
            <span className="text-gray-500">Created</span>
            <p>{new Date(widget.created_at).toLocaleString()}</p>
          </div>
          <div>
            <span className="text-gray-500">Updated</span>
            <p>{new Date(widget.updated_at).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Widget Stats */}
      <WidgetStatsCard widgetId={widget.id} widgetType={widget.type} />

      {/* Share Image Backgrounds */}
      <ShareBackgrounds widgetId={widget.id} />
    </div>
  );
}

function WidgetPreview({
  type,
  content,
}: {
  type: 'quiz' | 'poll';
  content: QuizContent | PollContent;
}) {
  return (
    <div className="widget-preview">
      <h3 className="hook-title">{content.hook_title}</h3>
      {type === 'quiz' ? (
        <QuizPreview content={content as QuizContent} />
      ) : (
        <PollPreview content={content as PollContent} />
      )}
    </div>
  );
}

function QuizPreview({ content }: { content: QuizContent }) {
  const [currentQ, setCurrentQ] = useState(0);
  const question = content.questions[currentQ];

  if (!question) return <p className="text-gray-500">No questions</p>;

  return (
    <div>
      <p className="progress-info">
        Question {currentQ + 1} of {content.questions.length}
      </p>
      <p className="question">{question.question}</p>
      <div className="options">
        {question.options.map((opt, i) => (
          <div
            key={i}
            className={`option ${i === question.correct_index ? 'correct' : ''}`}
          >
            <span className="option-marker">
              {i === question.correct_index && '✓'}
            </span>
            <span className="option-text">{opt}</span>
          </div>
        ))}
      </div>
      <div className="explanation">{question.explanation}</div>
      <div className="nav-buttons">
        <button
          className="nav-btn"
          disabled={currentQ === 0}
          onClick={() => setCurrentQ((q) => q - 1)}
        >
          ← Prev
        </button>
        <button
          className="nav-btn"
          disabled={currentQ === content.questions.length - 1}
          onClick={() => setCurrentQ((q) => q + 1)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function PollPreview({ content }: { content: PollContent }) {
  return (
    <div>
      <p className="question">{content.question}</p>
      <div className="options">
        {content.options.map((opt, i) => (
          <div key={i} className="option">
            <span className="option-marker" />
            <span className="option-text">{opt}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Widget stats component
function WidgetStatsCard({ widgetId, widgetType }: { widgetId: string; widgetType: string }) {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['widget-stats', widgetId],
    queryFn: () => statsApi.widget(widgetId),
  });

  return (
    <div className="card mt-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Stats</h2>
      {isLoading ? (
        <div className="text-center py-4 text-gray-500">Loading stats...</div>
      ) : !stats ? (
        <div className="text-center py-4 text-gray-500">No stats available</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Starts</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_starts.toLocaleString()}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Completions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_completions.toLocaleString()}</p>
              {stats.total_starts > 0 && (
                <p className="text-xs text-gray-500">{Math.round((stats.total_completions / stats.total_starts) * 100)}% of starts</p>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Shares</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_shares.toLocaleString()}</p>
              {stats.total_completions > 0 && (
                <p className="text-xs text-gray-500">{Math.round((stats.total_shares / stats.total_completions) * 100)}% of completions</p>
              )}
            </div>
          </div>

          {/* Poll results breakdown */}
          {widgetType === 'poll' && stats.poll_results && stats.total_votes !== undefined && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Poll Results ({stats.total_votes.toLocaleString()} vote{stats.total_votes !== 1 ? 's' : ''})
              </h3>
              <div className="space-y-2">
                {stats.poll_results.map((result, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700 truncate">{result.option}</span>
                        <span className="text-sm font-medium text-gray-900 ml-2">{result.percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${result.percentage}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 w-12 text-right">{result.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Share image backgrounds component
function ShareBackgrounds({ widgetId }: { widgetId: string }) {
  const queryClient = useQueryClient();
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [modalImage, setModalImage] = useState<{ bucket: string; label: string; url: string } | null>(null);

  const { data: backgroundsData, isLoading } = useQuery({
    queryKey: ['widget-backgrounds', widgetId],
    queryFn: () => widgetsApi.checkBackgrounds(widgetId),
  });

  // Force refresh images by adding timestamp
  const [refreshKey, setRefreshKey] = useState(Date.now());

  const generateMutation = useMutation({
    mutationFn: () => widgetsApi.generateBackgrounds(widgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widget-backgrounds', widgetId] });
      setImageErrors({}); // Reset errors to reload images
      setRefreshKey(Date.now()); // Force image reload
    },
  });

  // Buckets are now returned from the API (game-type specific)
  const buckets = backgroundsData?.buckets ?? [];
  const hasBackgrounds = backgroundsData?.hasBackgrounds ?? false;

  function handleRegenerate() {
    if (confirm('This will regenerate all share image backgrounds using AI. This may take a minute. Continue?')) {
      generateMutation.mutate();
    }
  }

  function openModal(bucket: { id: string; label: string }) {
    setModalImage({
      bucket: bucket.id,
      label: bucket.label,
      url: `${API_URL}/backgrounds/${widgetId}/${bucket.id}?t=${refreshKey}`,
    });
  }

  return (
    <div className="card mt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Share Image Backgrounds</h2>
          <p className="text-sm text-gray-500">
            AI-generated backgrounds for social share cards
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleRegenerate}
          disabled={generateMutation.isPending}
        >
          {generateMutation.isPending ? (
            <>
              <span className="animate-spin mr-2">⏳</span>
              Generating...
            </>
          ) : hasBackgrounds ? (
            '↻ Regenerate'
          ) : (
            '✨ Generate Backgrounds'
          )}
        </button>
      </div>

      {generateMutation.isError && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
          Failed to generate: {generateMutation.error.message}
        </div>
      )}

      {generateMutation.isSuccess && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-4">
          Backgrounds generated successfully!
          {generateMutation.data.count && ` (${generateMutation.data.count} images)`}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Checking backgrounds...</div>
      ) : !hasBackgrounds ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500 mb-2">No AI backgrounds generated yet</p>
          <p className="text-sm text-gray-400">
            Click "Generate Backgrounds" to create themed share images using AI
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {buckets.map((bucket) => (
            <div key={bucket.id} className="space-y-2">
              <p className="text-sm font-medium text-gray-700">{bucket.label}</p>
              <div
                className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                onClick={() => !imageErrors[bucket.id] && openModal(bucket)}
              >
                {imageErrors[bucket.id] ? (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                    Not available
                  </div>
                ) : (
                  <img
                    src={`${API_URL}/backgrounds/${widgetId}/${bucket.id}?t=${refreshKey}`}
                    alt={`${bucket.label} background`}
                    className="w-full h-full object-cover"
                    onError={() => setImageErrors(prev => ({ ...prev, [bucket.id]: true }))}
                    onLoad={() => setImageErrors(prev => ({ ...prev, [bucket.id]: false }))}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasBackgrounds && (
        <p className="text-xs text-gray-500 mt-4">
          These backgrounds are automatically used when users share their results. Click to enlarge.
        </p>
      )}

      {/* Image Modal */}
      {modalImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setModalImage(null)}
        >
          <div className="relative max-w-5xl w-full">
            <button
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-lg"
              onClick={() => setModalImage(null)}
            >
              ✕ Close
            </button>
            <p className="absolute -top-10 left-0 text-white text-sm">
              {modalImage.label}
            </p>
            <img
              src={modalImage.url}
              alt={`${modalImage.label} background`}
              className="w-full rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
